import { Star } from "lucide-react";
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import type { ProductionSlotsState } from "@/lib/sdtestapp/sdTestAppProductionClient";
import type { SdTestDevice } from "@/lib/sdtestapp/sdTestAppHttpClient";

import { BenchRackCanvas } from "./bench/BenchRackCanvas";
import type { ProductionTestDeviceStatus } from "./calTestApi";
import {
  readPolledDeviceFirmware,
  readPolledDeviceModelNumber,
  readPolledDeviceMcuUuid,
} from "./configurationDeviceFields";
import {
  productionTestScopeMessage,
  ProductionTestMatrix,
} from "./productionTestStatusUi";

function productionDutCount(slotsState: ProductionSlotsState | null): number {
  const referenceConnectorId = String(slotsState?.referenceConnectorId ?? "").trim();
  return (
    slotsState?.slots.filter((slot) => {
      const connectorId = String(slot.connectorId ?? "").trim();
      return connectorId && connectorId !== referenceConnectorId;
    }).length ?? 0
  );
}

export function ProductionRackPanel({
  slotsState,
  devices,
  selectedConnectorId,
  actionPending,
  canAssignSlots,
  showReferenceControls,
  productionTestStatusByMcuUuid,
  selectiveRetestAvailable,
  onSelectConnector,
  onAssignSlot,
  onSetReferenceDevice,
  onLogQcIncident,
  onStartSelectiveRetest,
}: {
  slotsState: ProductionSlotsState | null;
  devices: SdTestDevice[];
  selectedConnectorId: string | null;
  stepIndex?: number;
  actionPending: boolean;
  canAssignSlots: boolean;
  showReferenceControls: boolean;
  productionTestStatusByMcuUuid?: Map<string, ProductionTestDeviceStatus | null>;
  selectiveRetestAvailable?: boolean;
  onSelectConnector: (connectorId: string) => void;
  onAssignSlot: (slotName: string, connectorId: string | null, sourceSlotName?: string | null) => void;
  onSetReferenceDevice: (connectorId: string) => void;
  onLogQcIncident?: (connectorId: string) => void;
  onStartSelectiveRetest?: (connectorId: string, testKeys: string[]) => void | Promise<void>;
}): JSX.Element {
  const dutCount = productionDutCount(slotsState);
  const connectedCount = devices.length;
  const [selectedRetestKeysByConnector, setSelectedRetestKeysByConnector] = useState<Record<string, string[]>>({});

  const selectedDevice = useMemo(
    () => devices.find((device) => String(device.connectorId ?? device.cableSerialNumber ?? "") === selectedConnectorId) ?? null,
    [devices, selectedConnectorId],
  );
  const selectedMcuUuid = selectedDevice ? readPolledDeviceMcuUuid(selectedDevice) : "";
  const selectedStatus =
    selectedMcuUuid && productionTestStatusByMcuUuid
      ? productionTestStatusByMcuUuid.get(selectedMcuUuid.toUpperCase()) ?? null
      : null;
  const selectedRetestKeys = selectedConnectorId ? selectedRetestKeysByConnector[selectedConnectorId] ?? [] : [];

  const toggleRetestKey = (connectorId: string, testKey: string) => {
    setSelectedRetestKeysByConnector((current) => {
      const existing = new Set(current[connectorId] ?? []);
      if (existing.has(testKey)) {
        existing.delete(testKey);
      } else {
        existing.add(testKey);
      }
      return { ...current, [connectorId]: [...existing] };
    });
  };

  return (
    <section className="relative flex min-h-0 flex-col rounded border bg-card">
      <div className="flex flex-wrap items-center gap-2 px-4 pt-3 pr-24 text-sm">
        <Badge tone="muted">{connectedCount} connected</Badge>
        <Badge tone={slotsState?.hasReferenceDevice ? "success" : "warning"}>
          Reference: {slotsState?.hasReferenceDevice ? slotsState.referenceConnectorId ?? "set" : "not set"}
        </Badge>
        <div className="absolute right-3 top-3 z-10 rounded border bg-background/95 px-2.5 py-1.5 text-sm font-semibold shadow-sm">
          DUT: {dutCount}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4 pt-2">
        <BenchRackCanvas
          slots={slotsState?.slots ?? []}
          devices={devices}
          selectedConnectorId={selectedConnectorId}
          poolOnlyConnectorIds={slotsState?.poolOnlyConnectorIds ?? []}
          rackLayout={slotsState?.rackLayout}
          onSelectConnector={onSelectConnector}
          onAssignSlot={(slotName, connectorId, sourceSlotName) => {
            if (!canAssignSlots) return;
            onAssignSlot(slotName, connectorId, sourceSlotName);
          }}
          onLogQcIncident={canAssignSlots ? onLogQcIncident : undefined}
        />

        {devices.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Connected devices</p>
            <div className="grid max-h-56 gap-2 overflow-y-auto sm:grid-cols-2">
              {devices.map((device) => {
                const connectorId = String(device.connectorId ?? device.cableSerialNumber ?? "");
                const isReference = slotsState?.referenceConnectorId === connectorId;
                return (
                  <div key={connectorId} className="rounded border px-3 py-2 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        className="font-mono text-xs hover:underline"
                        onClick={() => onSelectConnector(connectorId)}
                      >
                        {connectorId}
                      </button>
                      {showReferenceControls ? (
                        <Button
                          type="button"
                          size="sm"
                          variant={isReference ? "primary" : "secondary"}
                          disabled={actionPending}
                          onClick={() => onSetReferenceDevice(connectorId)}
                        >
                          <Star className="mr-1 h-3 w-3" />
                          {isReference ? "Reference" : "Make reference"}
                        </Button>
                      ) : isReference ? (
                        <Badge tone="success">Reference</Badge>
                      ) : null}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {readPolledDeviceModelNumber(device)} · {readPolledDeviceFirmware(device)} · MCU{" "}
                      {readPolledDeviceMcuUuid(device) || "—"}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No devices detected yet. Connect devices to the coil — the rack updates automatically.
          </p>
        )}

        {selectedConnectorId && selectedStatus ? (
          <div className="space-y-2 rounded border bg-muted/20 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Production tests — {selectedConnectorId}
            </p>
            {productionTestScopeMessage(selectedStatus) ? (
              <p className="text-sm text-muted-foreground">{productionTestScopeMessage(selectedStatus)}</p>
            ) : null}
            <ProductionTestMatrix
              status={selectedStatus}
              selectable={Boolean(selectiveRetestAvailable && onStartSelectiveRetest && canAssignSlots)}
              selectedTestKeys={new Set(selectedRetestKeys)}
              onToggleTestKey={(testKey) => toggleRetestKey(selectedConnectorId, testKey)}
            />
            {selectiveRetestAvailable && onStartSelectiveRetest && selectedRetestKeys.length > 0 ? (
              <Button
                type="button"
                size="sm"
                disabled={actionPending || !canAssignSlots}
                onClick={() => void onStartSelectiveRetest(selectedConnectorId, selectedRetestKeys)}
              >
                Retest selected failed tests
              </Button>
            ) : selectiveRetestAvailable === false ? (
              <p className="text-xs text-muted-foreground">
                Selective retest requires a newer SDTestApp build with the production test retest endpoint.
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
